import Image from 'next/image';

import { type BoxProps, type TextProps, Box, Heading, LinkOverlay, Text } from '@chakra-ui/react';

import { ButtonLink } from '~/components/button-link';
import { PromoCard } from '~/features/experiments/ab_plp_redesign_2025/components/promo-card';
import { CircleArrowRightIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';

import { type FlashSaleEyebrowIcon, FlashSaleEyebrow } from './flash-sale-eyebrow';

type PromoStampSize = { height: number; width: number };

const DEFAULT_STAMP_OFFSET_RIGHT_PX = 32;

export type FlashSalePromoCardProps = {
  backgroundGradient: string;
  backgroundImageBlendMode?: BoxProps['mixBlendMode'];
  backgroundImageDesktop: string;
  backgroundImageMobile: string;
  backgroundImageMobileObjectPosition?: string;
  backgroundImageOpacity?: BoxProps['opacity'];
  checkoutHref: string;
  ctaColor: string;
  ctaLabel: string;
  eyebrowColor: string;
  eyebrowIcon?: FlashSaleEyebrowIcon;
  eyebrowLabel?: string;
  headline: string;
  headlineLine2?: string;
  id: string;
  imagePriority?: boolean;
  stampImageDesktop?: string;
  stampImageMobile?: string;
  stampOffsetRightDesktop?: number;
  stampSizeDesktop?: PromoStampSize;
  stampSizeMobile?: PromoStampSize;
  subtitle: string;
  subtitleColor: string;
  subtitleFontSize?: TextProps['fontSize'];
  onClick?: () => void;
};

export function FlashSalePromoCard({
  backgroundGradient,
  backgroundImageBlendMode,
  backgroundImageDesktop,
  backgroundImageMobile,
  backgroundImageMobileObjectPosition,
  backgroundImageOpacity,
  checkoutHref,
  ctaColor,
  ctaLabel,
  eyebrowColor,
  eyebrowIcon,
  eyebrowLabel,
  headline,
  headlineLine2,
  id,
  imagePriority,
  stampImageDesktop,
  stampImageMobile,
  stampOffsetRightDesktop = DEFAULT_STAMP_OFFSET_RIGHT_PX,
  stampSizeDesktop,
  stampSizeMobile,
  subtitle,
  subtitleColor,
  subtitleFontSize,
  onClick,
}: FlashSalePromoCardProps) {
  const hasStamp = Boolean(stampImageDesktop && stampImageMobile);
  const contentMarginRightPx = stampSizeDesktop
    ? Math.max(stampOffsetRightDesktop + stampSizeDesktop.width - 16, 240)
    : 240;

  return (
    <PromoCard
      bgGradient={backgroundGradient}
      eyebrowSlot={<FlashSaleEyebrow color={eyebrowColor} icon={eyebrowIcon} label={eyebrowLabel} />}
      id={id}
      padding={{ base: '32px', md: '48px 48px 56px' }}
      backgroundImageSlot={
        <>
          <ResponsiveBackground
            blendMode={backgroundImageBlendMode}
            desktopSrc={backgroundImageDesktop}
            imagePriority={imagePriority}
            mobileObjectPosition={backgroundImageMobileObjectPosition}
            mobileSrc={backgroundImageMobile}
            opacity={backgroundImageOpacity}
          />
          {stampImageDesktop && stampImageMobile ? (
            <DesktopStamp
              desktopSize={stampSizeDesktop}
              desktopSrc={stampImageDesktop}
              imagePriority={imagePriority}
              offsetRight={stampOffsetRightDesktop}
            />
          ) : null}
        </>
      }
      buttonSlot={
        <ButtonLink
          linkProps={{ href: checkoutHref }}
          buttonProps={{
            backgroundColor: ctaColor,
            color: 'white',
            colorScheme: 'blue',
            leftIcon: <CircleArrowRightIcon boxSize='18px' color='white' aria-hidden />,
            position: 'relative',
            size: 'icon-left',
            variant: 'solid',
            width: { base: '100%', md: '152px' },
            zIndex: 1,
            onClick,
          }}
        >
          {ctaLabel}
        </ButtonLink>
      }
      contentSlot={
        <Box marginRight={hasStamp ? { base: 0, lg: `${contentMarginRightPx}px` } : undefined}>
          <Heading
            as='h2'
            color='white'
            fontSize={{ base: '1.75rem', md: '2.25rem' }}
            fontWeight='600'
            id={id}
            letterSpacing='-0.01em'
            lineHeight={{ base: 1.15, md: 1.1 }}
            marginBottom={3}
          >
            <LinkOverlay href={checkoutHref} onClick={onClick}>
              {headline}
              {headlineLine2 ? (
                <Box as='span' display={{ base: 'block', md: 'inline' }}>
                  {` ${headlineLine2}`}
                </Box>
              ) : null}
            </LinkOverlay>
          </Heading>
          <Text color={subtitleColor} fontSize={subtitleFontSize ?? { base: '0.875rem', md: 'md' }} lineHeight={1.5}>
            {subtitle}
          </Text>
          {stampImageMobile ? (
            <Box
              display={{ base: 'block', lg: 'none' }}
              height={`${stampSizeMobile?.height ?? 132}px`}
              marginTop={6}
              maxWidth='100%'
              position='relative'
              width={`${stampSizeMobile?.width ?? 112}px`}
            >
              <Image
                alt=''
                priority={imagePriority}
                sizes={`${stampSizeMobile?.width ?? 112}px`}
                src={stampImageMobile}
                style={{ objectFit: 'contain', objectPosition: 'left center' }}
                fill
              />
            </Box>
          ) : null}
        </Box>
      }
    />
  );
}

function ResponsiveBackground({
  blendMode,
  desktopSrc,
  mobileSrc,
  imagePriority,
  mobileObjectPosition,
  opacity,
}: {
  blendMode?: BoxProps['mixBlendMode'];
  desktopSrc: string;
  mobileSrc: string;
  imagePriority?: boolean;
  mobileObjectPosition?: string;
  opacity?: BoxProps['opacity'];
}) {
  return (
    <Box
      bottom={0}
      left={0}
      mixBlendMode={blendMode}
      opacity={opacity}
      pointerEvents='none'
      position='absolute'
      right={0}
      top={0}
      zIndex={0}
    >
      <Box display={{ base: 'block', md: 'none' }} height='100%' position='relative' width='100%'>
        <Image
          alt=''
          loading='eager'
          priority={imagePriority}
          sizes='100vw'
          src={mobileSrc}
          style={{ objectFit: 'cover', objectPosition: mobileObjectPosition ?? 'right center' }}
          fill
        />
      </Box>
      <Box display={{ base: 'none', md: 'block' }} height='100%' position='relative' width='100%'>
        <Image
          alt=''
          loading='eager'
          priority={imagePriority}
          sizes='768px'
          src={desktopSrc}
          style={{ objectFit: 'cover', objectPosition: 'right center' }}
          fill
        />
      </Box>
    </Box>
  );
}

function DesktopStamp({
  desktopSrc,
  desktopSize = { height: 208, width: 192 },
  imagePriority,
  offsetRight,
}: {
  desktopSrc: string;
  desktopSize?: PromoStampSize;
  imagePriority?: boolean;
  offsetRight: number;
}) {
  return (
    <Box
      display={{ base: 'none', lg: 'block' }}
      height={`${desktopSize.height}px`}
      pointerEvents='none'
      position='absolute'
      right={`${offsetRight}px`}
      top='50%'
      transform='translateY(-50%)'
      width={`${desktopSize.width}px`}
      zIndex={1}
    >
      <Image
        alt=''
        priority={imagePriority}
        sizes={`${desktopSize.width}px`}
        src={desktopSrc}
        style={{ objectFit: 'contain' }}
        fill
      />
    </Box>
  );
}
